import { useState } from 'react';
import type { ServiceCategory } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { ServiceCategoryFormModal } from './ServiceCategoryFormModal.js';
import { useServiceCategories } from './api.js';

export function ServiceCategoriesPage(): JSX.Element {
  const { can } = useAuth();
  const canEdit = can('products', 'edit');

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceCategory | null>(null);

  const query = useServiceCategories({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    search: search || undefined,
  });

  const categories = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const handleSearchSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (category: ServiceCategory): void => {
    setEditing(category);
    setFormOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Service Categories"
        description="Manage billable labor types and their default hourly rates."
        actions={
          canEdit ? (
            <Button onClick={openCreate}>
              <Icon name="serviceCategories" className="h-4 w-4" />
              New service category
            </Button>
          ) : undefined
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-4">
          <form onSubmit={handleSearchSubmit} className="flex max-w-md items-center gap-2">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name…"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
        </div>

        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 text-right font-medium">Default rate</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={3}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={categories.length === 0}
              emptyTitle="No service categories found"
              emptyDescription={
                search
                  ? 'Try a different search term.'
                  : 'Create your first service category to get started.'
              }
              emptyAction={
                canEdit && !search ? (
                  <Button onClick={openCreate}>New service category</Button>
                ) : undefined
              }
            >
              {categories.map((category) => (
                <tr
                  key={category.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(category)}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">{category.name}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    {category.defaultRate}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={category.isActive ? 'green' : 'gray'}>
                      {category.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && categories.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <ServiceCategoryFormModal
        isOpen={formOpen}
        serviceCategory={editing}
        onClose={() => setFormOpen(false)}
      />
    </div>
  );
}
